// src/components/optimiseur/ValidationCandidat.jsx — LE SUIVI DU CANDIDAT.
//
// Le lien se crée depuis « Demander au candidat » (DemanderCandidat.jsx) ; ici,
// ce qu'il en est advenu : lien ouvert, réponses aux questions (ce que Leman
// en a écrit dans le CV, ce qui est « pas fait », ce qui est à relire), ses
// décisions sur les ajouts déjà écrits (valide / corrige / refuse) et ses
// commentaires. Corrections et commentaires attendent la relecture du
// recruteur avant d'entrer dans le CV.
//
//   GET    /besoins/:bid/cv-validation/:cid → { lien, questions, reponses, commentaires, a_relire }
//   POST   /besoins/:bid/cv-validation/:cid/relire { corrections, commentaires_lus } → { message, a_regenerer }
//   DELETE /besoins/:bid/cv-validation/:cid/lien
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { appel, dateCourte, dateHeure } from './api';

const BI = ({ icon }) => <i className={`bi bi-${icon}`} aria-hidden="true" />;
const STATUT = { ouvert: 'Lien ouvert', repondu: 'Le candidat a répondu', expire: 'Lien expiré', revoque: 'Lien révoqué' };
const DECISION = { valide: 'Validé', corrige: 'Corrigé', refuse: 'Refusé' };
// Ce que Leman a fait de chaque réponse aux questions.
const RESULTAT = {
  ecrit: ['Écrit dans le CV', ' opt-puce--ok'],
  pas_fait: ['Pas fait', ''],
  a_relire: ['À relire', ' opt-puce--confirmer'],
  en_cours: ['Leman écrit…', ''],
  sans_reponse: ['Sans réponse', ''],
  attente: ['En attente', ''],
};
const POLL_MS = 5000;
const POLL_MAX = 36; // trois minutes au plus

export default function ValidationCandidat({ besoinId, candidateId, version = 0, onRegenerer, onReponses, onLemanFini, onReprendre, showNotification }) {
  const [suivi, setSuivi] = useState({ etat: 'loading' }); // loading | route | error | ready
  const [relecture, setRelecture] = useState({}); // ajout_id → { integrer, texte }
  const [busy, setBusy] = useState(null);

  // Les fonctions de rappel du parent changent à chaque rendu : on garde les
  // dernières dans une référence, la lecture n'en dépend pas.
  const rappel = useRef({ onReponses, onLemanFini });
  rappel.current = { onReponses, onLemanFini };
  const minuterie = useRef(null);
  const tours = useRef(0);
  const enCoursAvant = useRef(false);
  const reponduVu = useRef(false);
  const lire = useCallback(async () => {
    clearTimeout(minuterie.current);
    try {
      const { r, j, absente } = await appel(`/besoins/${besoinId}/cv-validation/${candidateId}`);
      if (absente) { setSuivi({ etat: 'route' }); return; }
      if (!r.ok || j.success === false) throw new Error(j.error || `Le suivi n'a pas pu être lu (${r.status}).`);
      setSuivi({ etat: 'ready', ...j });
      // Une validation du candidat compte dans le score en l'état : l'analyse
      // se relit dès que ses réponses sont là.
      if (j.lien?.statut === 'repondu' && !reponduVu.current) { reponduVu.current = true; rappel.current.onReponses?.(); }
      const corr = (j.reponses || []).filter(x => x.decision === 'corrige');
      setRelecture(Object.fromEntries(corr.map(x => [x.ajout_id, { integrer: true, texte: x.correction || x.texte || '' }])));
      // Leman écrit les réponses du candidat en fond : on relit jusqu'à la fin,
      // puis le score se relit et le CV est à régénérer.
      const enCours = !!j.leman_en_cours || (j.questions || []).some(q => q.resultat === 'en_cours');
      if (enCours && tours.current < POLL_MAX) { tours.current += 1; minuterie.current = setTimeout(lire, POLL_MS); }
      if (enCoursAvant.current && !enCours) rappel.current.onLemanFini?.();
      enCoursAvant.current = enCours;
    } catch (e) { setSuivi({ etat: 'error', erreur: e.message }); }
  }, [besoinId, candidateId]);
  useEffect(() => { tours.current = 0; setSuivi(s => (s.etat === 'ready' ? s : { etat: 'loading' })); lire(); return () => clearTimeout(minuterie.current); }, [lire, version]);

  const revoquer = async () => {
    if (!window.confirm('Révoquer ce lien ? Le candidat ne pourra plus l’ouvrir.')) return;
    setBusy('revoquer');
    try {
      const { r, j, absente } = await appel(`/besoins/${besoinId}/cv-validation/${candidateId}/lien`, { method: 'DELETE' });
      if (absente) throw new Error('La révocation arrive côté serveur.');
      if (!r.ok || j.success === false) throw new Error(j.error || `Le lien n'a pas pu être révoqué (${r.status}).`);
      showNotification?.(j.message || 'Lien révoqué.', 'success');
      await lire();
    } catch (e) { showNotification?.(e.message, 'error'); }
    finally { setBusy(null); }
  };

  const relire = async () => {
    setBusy('relire');
    try {
      const corrections = Object.entries(relecture).map(([id, v]) => ({ ajout_id: Number(id) || id, integrer: !!v.integrer, texte: String(v.texte || '').trim() }));
      const { r, j, absente } = await appel(`/besoins/${besoinId}/cv-validation/${candidateId}/relire`, { method: 'POST', corps: { corrections, commentaires_lus: true } });
      if (absente) throw new Error('La relecture des réponses arrive côté serveur.');
      if (!r.ok || j.success === false) throw new Error(j.error || `La relecture a été refusée (${r.status}).`);
      showNotification?.(j.message || 'Relecture enregistrée.', 'success');
      await lire();
      if (j.a_regenerer) await onRegenerer?.();
      else onReponses?.();
    } catch (e) { showNotification?.(e.message, 'error'); }
    finally { setBusy(null); }
  };

  if (suivi.etat === 'route') return null;
  if (suivi.etat === 'loading') return <p className="opt-muet"><i className="bi bi-arrow-repeat tq-spin" aria-hidden="true" /> Lecture du suivi du candidat…</p>;
  if (suivi.etat === 'error') return <p className="opt-erreur">{suivi.erreur}</p>;

  const lien = suivi.lien || null;
  const actif = lien && lien.statut === 'ouvert';
  const questions = Array.isArray(suivi.questions) ? suivi.questions : [];
  const reponses = suivi.reponses || [];
  // Les commentaires déjà lus ne reviennent pas « à relire ».
  const commentaires = suivi.commentaires_lus ? [] : (suivi.commentaires || []);
  // À relire ici : ses corrections pas encore relues et ses commentaires. Une
  // réponse à une question « à relire » se reprend sur sa ligne (« Reprendre »).
  const corrections = reponses.filter(x => x.decision === 'corrige' && !x.relu);
  const aRelire = corrections.length + commentaires.length;
  if (!lien && !questions.length && !reponses.length) return null;

  return (
    <div className="opt-validation">
      <h4 className="opt-sous-titre">Suivi du candidat</h4>
      {lien && (
        <p className={`opt-muet${lien.statut === 'repondu' ? ' opt-ok' : ''}`}>
          <BI icon={lien.statut === 'repondu' ? 'check-circle-fill' : lien.statut === 'ouvert' ? 'link-45deg' : 'slash-circle'} />{' '}
          {STATUT[lien.statut] || lien.statut}
          {lien.statut === 'ouvert' && lien.expire_le ? ` — expire le ${dateCourte(lien.expire_le)}` : ''}
          {lien.ouvert_le ? ` · ouvert le ${dateHeure(lien.ouvert_le)}` : ''}
          {lien.repondu_le ? ` · réponses le ${dateHeure(lien.repondu_le)}` : ''}
          {actif && <>{' '}<button type="button" className="tuto-bulle__lien" onClick={revoquer} disabled={!!busy}><BI icon="slash-circle" /> Révoquer le lien</button></>}
        </p>
      )}

      {questions.length > 0 && (
        <div className="opt-validation__reponses">
          {questions.map(q => {
            const [libelle, ton] = RESULTAT[q.resultat || 'attente'] || [q.resultat, ''];
            return (
              <div key={q.critere} className="opt-reponse">
                <span className={`opt-puce${ton}`}>{q.resultat === 'en_cours' && <i className="bi bi-arrow-repeat tq-spin" aria-hidden="true" />} {libelle}</span>
                <div className="opt-reponse__corps">
                  <div className="opt-reponse__critere">{q.question || q.critere}</div>
                  {q.reponse && <div className="opt-reponse__texte">Sa réponse : « {q.reponse} »</div>}
                  {q.resultat === 'ecrit' && q.texte && <div className="opt-ok">Écrit{q.mission_libelle ? ` dans ${q.mission_libelle}` : ''} : {q.texte}</div>}
                  {q.resultat === 'pas_fait' && <div className="opt-muet">Il ne l’a pas fait : le point n’est plus demandé.</div>}
                  {q.resultat === 'a_relire' && (
                    <>
                      {q.raison && <div className="opt-note">{q.raison}</div>}
                      <button type="button" className="tuto-bulle__lien" onClick={() => onReprendre?.(q.critere, q.reponse || '')}>
                        <BI icon="telephone" /> Reprendre avec sa réponse
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {reponses.length > 0 && (
        <div className="opt-validation__reponses">
          <h4 className="opt-sous-titre">Ses décisions sur les ajouts</h4>
          {reponses.map(x => (
            <div key={x.ajout_id} className="opt-reponse">
              <span className={`opt-puce${x.decision === 'valide' ? ' opt-puce--ok' : x.decision === 'refuse' ? ' opt-puce--indispensable' : ' opt-puce--confirmer'}`}>{DECISION[x.decision] || x.decision}</span>
              <div className="opt-reponse__corps">
                <div className="opt-reponse__critere">{x.critere}</div>
                <div className="opt-reponse__texte">{x.texte}</div>
                {x.decision === 'valide' && x.le && <div className="opt-ok">Validé par le candidat le {dateHeure(x.le)}</div>}
                {x.decision === 'refuse' && <div className="opt-muet">Retiré du CV : le candidat ne s’y reconnaît pas.</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {aRelire > 0 && (
        <div className="opt-validation__relire">
          <h4 className="opt-sous-titre">À relire ({aRelire})</h4>
          <p className="opt-note">Ses corrections et commentaires n’entrent dans le CV qu’après votre relecture.</p>
          {corrections.map(x => {
            const v = relecture[x.ajout_id] || { integrer: true, texte: x.correction || '' };
            return (
              <div key={`c:${x.ajout_id}`} className="opt-carte">
                <div className="opt-carte__tete"><span className="opt-carte__critere">{x.critere}</span></div>
                <p className="opt-carte__citation">Proposé : « {x.texte} »</p>
                <textarea className="erp-field" rows={2} value={v.texte} disabled={!!busy || !v.integrer}
                  aria-label={`Correction du candidat pour « ${x.critere} »`}
                  onChange={e => setRelecture(s => ({ ...s, [x.ajout_id]: { ...v, texte: e.target.value } }))} />
                <div className="opt-choix" role="group" aria-label="Décision">
                  <button type="button" aria-pressed={v.integrer} disabled={!!busy} onClick={() => setRelecture(s => ({ ...s, [x.ajout_id]: { ...v, integrer: true } }))}><BI icon="check2" /> Intégrer</button>
                  <button type="button" aria-pressed={!v.integrer} disabled={!!busy} onClick={() => setRelecture(s => ({ ...s, [x.ajout_id]: { ...v, integrer: false } }))}><BI icon="x-lg" /> Ignorer</button>
                </div>
              </div>
            );
          })}
          {commentaires.map((c, i) => (
            <div key={`m:${i}`} className="opt-commentaire">
              {c.ligne_texte && <p className="opt-carte__citation">« {c.ligne_texte} »</p>}
              <p className="opt-commentaire__texte"><BI icon="chat-left-text" /> {c.texte}</p>
              {c.le && <p className="opt-note">{dateHeure(c.le)}</p>}
            </div>
          ))}
          <div className="opt-confirmer__pied">
            <span className="tuto-bulle__espace" />
            <button type="button" className="erp-btn erp-btn--primary erp-btn--sm" onClick={relire} disabled={!!busy}>
              {busy === 'relire' ? <i className="bi bi-arrow-repeat tq-spin" aria-hidden="true" /> : <BI icon="check2-all" />} {corrections.length ? 'Enregistrer la relecture' : 'Marquer comme lus'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
